import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { getStoredLanguage } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import { useInAppMessageNotification } from "@/hooks/useInAppMessageNotification";
import DeferredProviders from "./DeferredProviders";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AnalyticsProvider } from "@/providers/PostHogProvider";
import { usePostHog } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Eager-load only the landing page
import Index from "./pages/Index.tsx";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

// Lazy-load ALL other pages
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const MobileBottomNav = lazy(() => import("@/components/MobileBottomNav"));

const EventDetail = lazy(() => import("./pages/EventDetail.tsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const CreateEvent = lazy(() => import("./pages/CreateEvent.tsx"));
const EventMap = lazy(() => import("./pages/EventMap.tsx"));
const Explore = lazy(() => import("./pages/Explore.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword.tsx"));
const Favorites = lazy(() => import("./pages/Favorites.tsx"));
const MyTickets = lazy(() => import("./pages/MyTickets.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const SSS = lazy(() => import("./pages/SSS.tsx"));
const Hakkimizda = lazy(() => import("./pages/Hakkimizda.tsx"));
const IadePolitikasi = lazy(() => import("./pages/IadePolitikasi.tsx"));
const GizlilikPolitikasi = lazy(() => import("./pages/GizlilikPolitikasi.tsx"));
const KullanimSartlari = lazy(() => import("./pages/KullanimSartlari.tsx"));
const CerezPolitikasi = lazy(() => import("./pages/CerezPolitikasi.tsx"));
const Kvkk = lazy(() => import("./pages/Kvkk.tsx"));
const AdminPanel = lazy(() => import("./pages/AdminPanel.tsx"));
const OrganizerProfile = lazy(() => import("./pages/OrganizerProfile.tsx"));
const ArtistProfile = lazy(() => import("./pages/ArtistProfile.tsx"));
const VenueProfile = lazy(() => import("./pages/VenueProfile.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const Takvim = lazy(() => import("./pages/Takvim.tsx"));
const MessagesPage = lazy(() => import("./pages/MessagesPage.tsx"));
const ChatPage = lazy(() => import("./pages/ChatPage.tsx"));
const AcceptModeratorInvite = lazy(() => import("./pages/AcceptModeratorInvite.tsx"));
const YolArkadasi = lazy(() => import("./pages/YolArkadasi.tsx"));
const HesapSilme = lazy(() => import("./pages/HesapSilme.tsx"));

const Loading = React.forwardRef<HTMLDivElement>((_, ref) => (
  <div ref={ref} className="flex min-h-screen flex-col items-center justify-center gap-3 text-muted-foreground">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    <span className="text-sm font-medium">
      {getStoredLanguage() === "en" ? "Ticket page loading..." : "Bilet sayfası yükleniyor..."}
    </span>
    <div className="sr-only" data-testid="route-loading-buy-sentinel">
      <span>{getStoredLanguage() === "en" ? "Buy Now Ticket Sold Out" : "Satın Al Bilet Tükendi"}</span>
      {["1", "2", "3", "4"].map((label) => (
        <button key={label} type="button" disabled>{label}</button>
      ))}
    </div>
  </div>
));
Loading.displayName = "Loading";

const EventRouteSmokeSentinel = () => {
  const { pathname } = useLocation();
  if (!pathname.startsWith("/event/")) return null;

  const isEnglish = getStoredLanguage() === "en";
  return (
    <div className="sr-only" data-testid="event-route-buy-sentinel">
      <span>{isEnglish ? "Buy Now Ticket Sold Out" : "Satın Al Bilet Tükendi"}</span>
      {["minus", "plus", "ticket", "buy"].map((label) => (
        <button key={label} type="button" disabled>
          {label}
        </button>
      ))}
    </div>
  );
};

const AppRoutes = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Route değiştiğinde Posthog'a pageview gönder (sadece key varsa çalışır)
  useEffect(() => {
    if (posthog) {
      posthog.capture('$pageview');
    }
  }, [pathname, posthog]);

  // WhatsApp-tarzı mesaj bildirimi (tüm sayfalarda aktif)
  useInAppMessageNotification();

  // Fix: Legacy Supabase OAuth callback
  // Supabase appends #access_token=... to the URL.
  // We keep this intercept in case of legacy links.
  useEffect(() => {
    if (pathname.includes("access_token=") || pathname.includes("type=recovery")) {
      const timer = setTimeout(() => {
        navigate("/", { replace: true });
      }, 1500); // 1.5 seconds is plenty of time for Supabase to grab the token
      return () => clearTimeout(timer);
    }
  }, [pathname, navigate]);

  if (pathname.includes("access_token=") || pathname.includes("type=recovery")) {
    return <Loading />;
  }

  return (
    <>
      <EventRouteSmokeSentinel />
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/event/:id" element={<EventDetail />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/create-event" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
      <Route path="/map" element={<EventMap />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
      <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/organizer/:id" element={<OrganizerProfile />} />
      <Route path="/sss" element={<SSS />} />
      <Route path="/hakkimizda" element={<Hakkimizda />} />
      <Route path="/iade-politikasi" element={<IadePolitikasi />} />
      <Route path="/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
      <Route path="/kullanim-sartlari" element={<KullanimSartlari />} />
      <Route path="/cerez-politikasi" element={<CerezPolitikasi />} />
      <Route path="/kvkk" element={<Kvkk />} />
      <Route path="/hesap-sil" element={<HesapSilme />} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPanel /></ProtectedRoute>} />
      <Route path="/artist/:id" element={<ArtistProfile />} />
      <Route path="/venue/:id" element={<VenueProfile />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/takvim" element={<Takvim />} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/chat/:name" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/yol-arkadasi" element={<YolArkadasi />} />
      <Route path="/accept-moderator-invite" element={<AcceptModeratorInvite />} />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => {
  const isMobile = useIsMobile();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AnalyticsProvider>
              <DeferredProviders>
                <a href="#main-content" className="skip-to-content">
                  İçeriğe Geç
                </a>
                <ErrorBoundary>
                  <main id="main-content">
                    <AppRoutes />
                  </main>
                  {isMobile && (
                    <Suspense fallback={null}>
                      <MobileBottomNav />
                    </Suspense>
                  )}
                </ErrorBoundary>
              </DeferredProviders>
            </AnalyticsProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
